import { useState } from 'react'
import { useQuery } from 'react-query'
import { Add } from '@mui/icons-material'
import { Button, DeleteIcon, EditIcon, Table } from 'components'
import { User } from 'types'
import useUserService from 'services/userService'
import { defaultFilterParams, IFilterParams } from 'types/models/FilterSort'
import { usersTableColumns } from './userColumns'
import useAddEditUserModal from './dialogs/useAddEditUserModal'
import useDeleteUserModal from './dialogs/useDeleteModal'
import { Container, ScorollerBox, TableWrapper } from './Users.styled'
import {
  CrudAction,
  Entity,
  usePageHeader,
  usePagination,
  usePermissions,
} from '../../utils/hooks'
import { TableActionConfig } from '../../components/Table/types'
import { formatFiltersSorts } from '../../components/Table/TableFilter/helpers'

const Users = () => {
  const { checkPermission } = usePermissions()
  const { getUsers } = useUserService()
  const { currentPage, setCurrentPage, rowsPerPage, setRowsPerPage } =
    usePagination({})

  const onPaginationChange = (page: number, perPage: number) => {
    setCurrentPage(page)
    setRowsPerPage(perPage)
  }

  const [filterParams, setFilterParams] =
    useState<IFilterParams>(defaultFilterParams)

  const { data: users } = useQuery({
    queryKey: ['users-list', filterParams, currentPage, rowsPerPage],
    queryFn: () =>
      getUsers(filterParams, {
        currentPage: currentPage,
        itemsPerPage: rowsPerPage,
      }),
    retry: false,
  })

  const [addEditModal, toggleAddEditModal] = useAddEditUserModal({})
  const [deleteModal, toggleDeleteModal] = useDeleteUserModal({})

  usePageHeader({
    headerTitle: 'Add User',
    actionButton: (
      <Button
        disabled={!checkPermission(Entity.USERS, CrudAction.CREATE)}
        startIcon={<Add />}
        onClick={() => toggleAddEditModal(undefined)}
      >
        Add User
      </Button>
    ),
  })

  const actions: TableActionConfig<User>[] = [
    {
      name: 'edit',
      icon: <EditIcon size={14} />,
      handler: (row: User) => {
        if (!row) return
        toggleAddEditModal(row)
      },
    },
    {
      name: 'delete',
      icon: <DeleteIcon size={20} />,
      handler: (row: User) => {
        if (!row?.id) return
        toggleDeleteModal(row.id)
      },
      permission: {
        entity: Entity.USERS,
        action: CrudAction.DELETE,
      },
    },
  ]

  return (
    <Container>
      <ScorollerBox>
        <TableWrapper>
          <Table
            handleGlobalFilter={(filter, sort) => {
              setFilterParams(formatFiltersSorts(filter, sort))
            }}
            handleGlobalSort={(sort, filter) => {
              setFilterParams(formatFiltersSorts(filter, sort))
            }}
            data={users?.results}
            columns={usersTableColumns({
              actions,
            })}
            onPaginationChange={onPaginationChange}
            showPagination={true}
            total={users?.pagination?.total || 0}
            tableKey="users"
          />
        </TableWrapper>
      </ScorollerBox>
      {addEditModal}
      {deleteModal}
    </Container>
  )
}

export default Users
